import { Capacitor } from "@capacitor/core";
import axios from "axios";
import Api from "../Api/Api.json";

let JStyle = null;
if (Capacitor.isNativePlatform()) {
  JStyle = require("../JStylePlugin").default;
}

let syncInProgress = false;
const statusListeners = new Set();

const emitStatus = (message) => {
  statusListeners.forEach((listener) => {
    try {
      listener(message);
    } catch (error) {
      console.error("[deviceSync] status listener error", error);
    }
  });
};

export const subscribeSyncStatus = (callback) => {
  statusListeners.add(callback);
  return () => statusListeners.delete(callback);
};

export const isDeviceSyncAvailable = () =>
  Capacitor.isNativePlatform() && JStyle !== null;

const getUserId = () => {
  const raw = localStorage.getItem("user_info");
  if (!raw) return null;
  try {
    return JSON.parse(raw).di_id ?? null;
  } catch {
    return null;
  }
};

const getPairedDeviceAddress = () => localStorage.getItem("jstyle_address");

export const syncWellnessData = async ({ userId, onStatus } = {}) => {
  const notify = onStatus ?? emitStatus;
  const id = userId ?? getUserId();

  if (!isDeviceSyncAvailable()) {
    notify("Data sync not available on web platform");
    return { ok: false, reason: "web" };
  }
  if (!id) {
    notify("Sync skipped: user not logged in");
    return { ok: false, reason: "no_user" };
  }

  notify("Sync started");

  let lastSleepTs = localStorage.getItem("last_sleep_datetime");
  let lastHrvTs = localStorage.getItem("last_hrv_datetime");
  if (lastHrvTs == null) lastHrvTs = "";
  if (lastSleepTs == null) lastSleepTs = "";

  const hrvResult = await JStyle.getHRVData({ date: lastHrvTs });
  localStorage.setItem("last_hrv_datetime", hrvResult.lasthrvtime);
  localStorage.setItem("last_hrv_data", hrvResult.hrvlist);

  const sleepResult = await JStyle.getSleepData({ date: lastSleepTs });
  localStorage.setItem("last_sleep_datetime", sleepResult.lastsleeptime);
  localStorage.setItem("last_sleep_data", sleepResult.sleeplist);
  notify("Read from device.");

  const hrvDataRead = localStorage.getItem("last_hrv_data");
  const headers = { headers: { "Content-Type": "application/json" } };
  const requestData = {
    task: "setSemionWellnessData",
    id,
    sleep_list: sleepResult.sleeplist,
    hrv_list: hrvDataRead === null ? [] : hrvDataRead,
  };

  const uploadResult = await axios.post(Api.FITNESS_API, requestData, headers);
  if (uploadResult.data === "updated") {
    notify("Uploaded Successfully.");
  } else {
    notify("Uploaded.");
  }

  return { ok: true, sleepResult, hrvResult };
};

export const syncActivityData = async ({ userId, onStatus } = {}) => {
  const notify = onStatus ?? emitStatus;
  const id = userId ?? getUserId();

  if (!isDeviceSyncAvailable()) {
    notify("Activity sync not available on web platform");
    return { ok: false, reason: "web" };
  }
  if (!id) {
    notify("Activity sync skipped: user not logged in");
    return { ok: false, reason: "no_user" };
  }

  const result = await JStyle.getTotalActivityData({ date: "" });
  const headers = { headers: { "Content-Type": "application/json" } };
  const requestData = {
    task: "setSemionTotalActivity",
    id,
    total_activity_list: result.totalactivitylist,
  };

  const uploadResult = await axios.post(Api.FITNESS_API, requestData, headers);
  if (uploadResult.data === "updated") {
    notify("Uploaded Activity Successfully.");
  } else {
    notify("Uploaded Activity.");
  }

  return { ok: true, result };
};

export const syncDeviceData = async ({ userId, onStatus, requireConnected = true } = {}) => {
  if (syncInProgress) {
    return { ok: false, skipped: true, reason: "in_progress" };
  }

  if (!isDeviceSyncAvailable()) {
    return { ok: false, reason: "web" };
  }

  if (requireConnected) {
    const { connected } = await JStyle.isConnected();
    if (!connected) {
      return { ok: false, reason: "not_connected" };
    }
  }

  syncInProgress = true;
  try {
    await syncWellnessData({ userId, onStatus });
    await syncActivityData({ userId, onStatus });
    return { ok: true };
  } catch (error) {
    const notify = onStatus ?? emitStatus;
    notify(`Sync failed: ${error?.message ?? String(error)}`);
    console.error("[deviceSync] syncDeviceData failed", error);
    return { ok: false, error };
  } finally {
    syncInProgress = false;
  }
};

export const tryAutoSync = async (reason = "manual", { onStatus } = {}) => {
  if (!isDeviceSyncAvailable()) {
    return { ok: false, skipped: true, reason: "web" };
  }

  const userId = getUserId();
  const deviceAddress = getPairedDeviceAddress();

  if (!userId || !deviceAddress) {
    return { ok: false, skipped: true, reason: "not_configured" };
  }

  console.log(`[deviceSync] tryAutoSync (${reason})`);

  try {
    const { connected } = await JStyle.isConnected();
    if (connected) {
      return syncDeviceData({ userId, onStatus });
    }

    await JStyle.connect({ deviceId: deviceAddress });
    return { ok: false, skipped: false, reason: "connecting" };
  } catch (error) {
    console.error(`[deviceSync] tryAutoSync (${reason}) failed`, error);
    return { ok: false, error };
  }
};

export const onDeviceConnected = async ({ onStatus } = {}) => {
  return syncDeviceData({ onStatus });
};
